/**
 * 动作注册表（M2-01 / DESIGN.md §7.4）。
 *
 * 「动作」是编辑器里一切可被用户触发的操作的统一形态：菜单项、快捷键、
 * 将来的命令面板（§6.6）枚举的都是**同一份注册表**——三处各自维护一份
 * 清单是菜单迷宫的起点。
 *
 * 分层约束（CLAUDE.md 规则 5）：文档修改类动作的 run 里只允许调
 * {@link ActionServices.dispatch}（走 CommandBus）与 undo/redo（走历史系统）。
 * test/app/actions.test.ts 会扫描本目录，确认没有其他 mutate 路径。
 */
import type { Command } from '../command-bus';
import type { Document } from '../../document/types';
import { matchesShortcut, type KeyEventLike, type Shortcut } from './keymap';

export type { Shortcut } from './keymap';

/** 菜单分组。顺序即菜单栏顺序（§7.1）。 */
export const MENU_GROUPS = [
  '文件',
  '编辑',
  '图像',
  '图层',
  '选择',
  '滤镜',
  '视图',
  '窗口',
  '帮助',
] as const;

export type MenuGroup = (typeof MENU_GROUPS)[number];

/** enabled 谓词消费的应用状态快照。 */
export interface AppState {
  readonly hasDocument: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly hasSelection: boolean;
  /** 存在可恢复的崩溃镜像。 */
  readonly hasRecoverable: boolean;
  /** 图层面板里有一个当前选中的图层（"复制图层"/"图层编组"这类以它为目标的动作要用）。 */
  readonly hasActiveLayer: boolean;
}

export const EMPTY_APP_STATE: AppState = {
  hasDocument: false,
  canUndo: false,
  canRedo: false,
  hasSelection: false,
  hasRecoverable: false,
  hasActiveLayer: false,
};

/**
 * 动作可用的服务面。文档修改只有 dispatch/undo/redo 三条路——
 * 这个接口上**没有**文档对象本身，动作拿不到可变引用。
 */
export interface ActionServices {
  readonly state: () => AppState;
  /** 当前文档的**只读**访问。修改仍然只有 dispatch 一条路。 */
  readonly document: () => Document | null;
  readonly dispatch: (command: Command) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  /** 状态栏提示。 */
  readonly hint: (text: string) => void;
}

export interface ActionDefinition {
  /** 稳定 id，形如 `edit.undo`。菜单、快捷键、测试都以它为锚。 */
  readonly id: string;
  /** 面向用户的标题（Adobe 中文版术语，§7.3）。 */
  readonly title: string;
  readonly group: MenuGroup;
  /** 组内分节。不同节之间菜单画分隔线。 */
  readonly section: number;
  /** 节内顺序。 */
  readonly order: number;
  readonly shortcut?: Shortcut;
  /** 缺省恒可用。 */
  readonly enabled?: (state: AppState) => boolean;
  /**
   * 二级子菜单标签（如「变换」）。同一组内 submenu 相同的动作会被折进一个
   * 二级弹出菜单，而不是各自平铺成组里的一行——照 Photoshop「编辑→变换→
   * 缩放/旋转/…」的样子。省略 = 平铺在组里（默认行为不变）。
   */
  readonly submenu?: string;
  readonly run: (services: ActionServices) => void | Promise<void>;
}

export class ActionRegistry {
  readonly #actions = new Map<string, ActionDefinition>();

  register(definition: ActionDefinition): void {
    if (this.#actions.has(definition.id)) {
      throw new Error(`动作 ${definition.id} 已注册。id 必须唯一，重复注册通常是装配错误。`);
    }
    this.#actions.set(definition.id, definition);
  }

  get(id: string): ActionDefinition | null {
    return this.#actions.get(id) ?? null;
  }

  /** 全部动作，按 组→节→序 稳定排序。菜单与命令面板都用它。 */
  list(): readonly ActionDefinition[] {
    return [...this.#actions.values()].sort((a, b) => {
      const groupDelta = MENU_GROUPS.indexOf(a.group) - MENU_GROUPS.indexOf(b.group);
      if (groupDelta !== 0) return groupDelta;
      if (a.section !== b.section) return a.section - b.section;
      if (a.order !== b.order) return a.order - b.order;
      return a.id < b.id ? -1 : 1;
    });
  }

  isEnabled(id: string, state: AppState): boolean {
    const action = this.#actions.get(id);
    if (action === undefined) return false;
    return action.enabled?.(state) ?? true;
  }

  /**
   * 执行动作。禁用状态下拒绝执行并返回 false——
   * 菜单置灰只是显示，这里才是真正的闸门（快捷键不经过菜单）。
   */
  execute(id: string, services: ActionServices): boolean {
    const action = this.#actions.get(id);
    if (action === undefined) return false;
    if (!(action.enabled?.(services.state()) ?? true)) return false;
    void action.run(services);
    return true;
  }

  /**
   * 键盘事件 → 动作。没有命中返回 null。
   * 只匹配带快捷键的动作；单字母工具键不在这里（M2-05）。
   */
  actionForKey(event: KeyEventLike): ActionDefinition | null {
    for (const action of this.#actions.values()) {
      if (action.shortcut !== undefined && matchesShortcut(action.shortcut, event)) {
        return action;
      }
    }
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* 命令面板（M4-10 / DESIGN.md §6.6）                                    */
/* ------------------------------------------------------------------ */

/** 命令面板一行的展示数据——比 ActionDefinition 少了 run，多了预算好的 enabled。 */
export interface PaletteEntry {
  readonly id: string;
  readonly title: string;
  readonly group: MenuGroup;
  readonly shortcut?: Shortcut;
  readonly enabled: boolean;
}

/** 把一份动作列表在当前状态下转成命令面板的展示数据，保持传入顺序。
 * `paletteEntries` 的共享实现——独立导出是因为调用方有时需要先把用户
 * 快捷键覆盖层（M5-03 `shortcut-overrides.ts`）套到动作列表上再转换，
 * 那种场景手上没有 `ActionRegistry` 实例（覆盖层产出的是普通数组），
 * 不能走 `paletteEntries(registry, state)` 这条必须传注册表的入口。 */
export function paletteEntriesFromActions(
  actions: readonly ActionDefinition[],
  state: AppState,
): readonly PaletteEntry[] {
  return actions.map((action) => ({
    id: action.id,
    // 命令面板是扁平列表，没有"先点开二级菜单"这个上下文——带上 submenu
    // 前缀（"变换 > 缩放"），不然搜索结果里一个孤零零的"缩放"不知道是什么
    title: action.submenu === undefined ? action.title : `${action.submenu} > ${action.title}`,
    group: action.group,
    ...(action.shortcut === undefined ? {} : { shortcut: action.shortcut }),
    enabled: action.enabled?.(state) ?? true,
  }));
}

/** 把注册表在当前状态下的全部动作转成命令面板的展示数据，保持 `list()` 的稳定顺序。 */
export function paletteEntries(registry: ActionRegistry, state: AppState): readonly PaletteEntry[] {
  return paletteEntriesFromActions(registry.list(), state);
}

/**
 * 模糊子序列匹配：query 的字符必须按顺序（不必连续）都出现在 text 里才算命中。
 * 分数偏好"连续匹配"——`"cv"` 匹配 `"曲线"` 里的字符不连续时分数低于
 * 匹配到相邻字符的情况，排序时更贴近直觉的候选排在前面。
 * 不命中返回 `null`（区别于分数 0——空 query 也可能得 0 分但仍算命中）。
 */
export function fuzzyMatch(query: string, text: string): number | null {
  if (query.trim() === '') return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let qi = 0;
  let score = 0;
  let consecutive = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      score += 1 + consecutive;
      consecutive++;
      qi++;
    } else {
      consecutive = 0;
    }
  }
  return qi === q.length ? score : null;
}

/** 按 query 过滤 + 排序（分数降序）；空 query 原样返回（保持菜单顺序）。 */
export function filterPaletteEntries(
  entries: readonly PaletteEntry[],
  query: string,
): readonly PaletteEntry[] {
  if (query.trim() === '') return entries;
  const scored: Array<{ entry: PaletteEntry; score: number }> = [];
  for (const entry of entries) {
    const score = fuzzyMatch(query, entry.title);
    if (score !== null) scored.push({ entry, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((s) => s.entry);
}
